import type { FC } from 'react';
import { Typography } from '@interstate/components/Typography';
import type { ConsumerSearchResult, SearchResultAddress } from '../../types/search.types';
import { ResultColumns } from './search.styles';
import LabelledField from './LabelledField';
import SearchResultRow from './SearchResultRow';

export interface ConsumerResultRowProps {
    entity: ConsumerSearchResult;
    isSelected: boolean;
    onSelect: (entity: ConsumerSearchResult) => void;
}

const fullName = (entity: ConsumerSearchResult) =>
    [entity.firstName, entity.middleName, entity.lastName, entity.suffix].filter(Boolean).join(' ');

const addressLines = (address?: SearchResultAddress) => {
    if (!address) return [];
    const cityLine = [address.city, address.stateOrProvinceAbbreviation, address.postalCode].filter(Boolean).join(', ');
    return [address.street1, address.street2, cityLine].filter(Boolean) as string[];
};

const ConsumerResultRow: FC<ConsumerResultRowProps> = ({ entity, isSelected, onSelect }) => {
    const name = fullName(entity);
    const lines = addressLines(entity.primaryAddress);

    return (
        <SearchResultRow
            testId={`consumer-search-result-${entity.commonConsumerId}`}
            isSelected={isSelected}
            onSelect={() => onSelect(entity)}
        >
            <ResultColumns>
                <div>
                    {name && (
                        <LabelledField label="Name">
                            <Typography variant="strong-md">{name}</Typography>
                        </LabelledField>
                    )}
                    {entity.cellPhone && (
                        <LabelledField label="Cell Phone">
                            <Typography variant="body-md">{entity.cellPhone}</Typography>
                        </LabelledField>
                    )}
                    {entity.homePhone && (
                        <LabelledField label="Home Phone">
                            <Typography variant="body-md">{entity.homePhone}</Typography>
                        </LabelledField>
                    )}
                    {entity.workPhone && (
                        <LabelledField label="Work Phone">
                            <Typography variant="body-md">{entity.workPhone}</Typography>
                        </LabelledField>
                    )}
                </div>
                <div>
                    {entity.primaryEmail && (
                        <LabelledField label="Email Address">
                            <Typography variant="body-md">{entity.primaryEmail}</Typography>
                        </LabelledField>
                    )}
                    {lines.length > 0 && (
                        <LabelledField label="Street Address">
                            {lines.map((line) => (
                                <Typography key={line} variant="body-md" sx={{ display: 'block' }}>
                                    {line}
                                </Typography>
                            ))}
                        </LabelledField>
                    )}
                </div>
            </ResultColumns>
        </SearchResultRow>
    );
};

export default ConsumerResultRow;
